/**
 * API Retry Service
 * 
 * Provides comprehensive retry mechanisms for failed API requests with:
 * - Exponential backoff
 * - Configurable retry attempts
 * - Network error detection
 * - Rate limit handling
 * - Circuit breaker pattern
 * - Request deduplication
 */

import { debugLog } from '../utils/debugLog';

type RetryErrorCode =
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'RATE_LIMITED'
  | 'SERVER_ERROR'
  | 'THROTTLED'
  | 'UNAVAILABLE'
  | 'INTERNAL_ERROR'
  | 'GRAPHQL_ERROR'
  | 'NO_DATA'
  | 'CLIENT_ERROR';

export interface RetryableError extends Error {
  status?: number;
  statusText?: string;
  code?: RetryErrorCode;
  graphqlErrors?: GraphQLErrorLike[];
}

interface GraphQLErrorLike {
  message: string;
  extensions?: {
    code?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface GraphQLResponse<T> {
  data?: T;
  errors?: GraphQLErrorLike[];
}

const enhanceError = <T extends Error>(
  error: T,
  metadata: Partial<RetryableError>
): T & RetryableError => Object.assign(error, metadata);

const getErrorCode = (error: Error): RetryErrorCode | undefined => {
  return (error as Partial<RetryableError>).code as RetryErrorCode | undefined;
};

// Retry configuration interface
export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number; // Base delay in milliseconds
  maxDelay: number; // Maximum delay in milliseconds
  backoffMultiplier: number; // Exponential backoff multiplier
  retryableErrors: RetryErrorCode[]; // Error codes/messages that should trigger retry
  shouldRetry?: (error: Error, attempt: number) => boolean; // Custom retry logic
}

// Default retry configuration
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  retryableErrors: [
    'NETWORK_ERROR',
    'TIMEOUT',
    'RATE_LIMITED',
    'SERVER_ERROR',
    'THROTTLED',
    'UNAVAILABLE',
    'INTERNAL_ERROR'
  ]
};

// Shopify-specific retry configuration
export const SHOPIFY_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 4,
  baseDelay: 500,
  maxDelay: 8000,
  backoffMultiplier: 2,
  retryableErrors: [
    'NETWORK_ERROR',
    'TIMEOUT',
    'THROTTLED',
    'RATE_LIMITED',
    'INTERNAL_ERROR',
    'UNAVAILABLE',
    'SERVER_ERROR'
  ]
};

// Request metadata for tracking
interface RequestMetadata {
  url: string;
  method: string;
  timestamp: number;
  attempt: number;
}

// Circuit breaker state
interface CircuitBreakerState {
  failures: number;
  lastFailureTime: number;
  state: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
}

// Request deduplication cache
interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

/**
 * API Retry Service Class
 */
export class ApiRetryService {
  private circuitBreakers: Map<string, CircuitBreakerState> = new Map();
  private pendingRequests: Map<string, PendingRequest<unknown>> = new Map();
  private readonly circuitBreakerThreshold = 5; // Failures before opening circuit
  private readonly circuitBreakerTimeout = 30000; // 30 seconds
  private readonly deduplicationTimeout = 5000; // 5 seconds

  private cloneIfPossible<T>(value: T): T {
    if (value && typeof (value as { clone?: unknown }).clone === 'function') {
      try {
        return (value as unknown as { clone: () => T }).clone();
      } catch {
        return value;
      }
    }

    return value;
  }

  /**
   * Execute a function with retry logic
   */
  async executeWithRetry<T>(
    fn: () => Promise<T>,
    config: Partial<RetryConfig> = {},
    metadata?: Partial<RequestMetadata>
  ): Promise<T> {
    const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
    const requestKey = this.generateRequestKey(metadata);
    const shouldDeduplicate =
      requestKey !== null && (metadata?.method || 'GET').toUpperCase() === 'GET';

    // Check circuit breaker
    if (requestKey !== null && this.isCircuitOpen(requestKey)) {
      throw new Error('Circuit breaker is open - service temporarily unavailable');
    }

    // Check for duplicate requests
    const existingRequest = requestKey
      ? (this.pendingRequests.get(requestKey) as PendingRequest<T> | undefined)
      : undefined;
    if (
      shouldDeduplicate &&
      existingRequest &&
      Date.now() - existingRequest.timestamp < this.deduplicationTimeout
    ) {
      debugLog('Deduplicating request:', requestKey);
      return existingRequest.promise.then((value) => this.cloneIfPossible(value));
    }

    let lastError: Error;
    let attempt = 0;

    const executeAttempt = async (): Promise<T> => {
      attempt++;
      
      try {
        const result = await fn();
        
        // Success - reset circuit breaker
        if (requestKey !== null) {
          this.resetCircuitBreaker(requestKey);
          this.pendingRequests.delete(requestKey);
        }
        
        return result;
      } catch (error) {
        lastError = error as Error;
        
        debugLog(`API request failed (attempt ${attempt}/${retryConfig.maxAttempts}):`, {
          error: lastError.message,
          metadata,
          attempt
        });

        // Check if we should retry
        if (attempt >= retryConfig.maxAttempts || !this.shouldRetryError(lastError, attempt, retryConfig)) {
          if (requestKey !== null) {
            this.recordFailure(requestKey);
            this.pendingRequests.delete(requestKey);
          }
          throw lastError;
        }

        // Calculate delay with exponential backoff
        const delay = this.calculateDelay(attempt, retryConfig);
        
        debugLog(`Retrying in ${delay}ms...`);
        await this.sleep(delay);
        
        return executeAttempt();
      }
    };

    // Store pending request for deduplication
    const promise = executeAttempt();
    if (shouldDeduplicate) {
      this.pendingRequests.set(requestKey, {
        promise: promise as Promise<unknown>,
        timestamp: Date.now()
      });
    }

    return shouldDeduplicate ? promise.then((value) => this.cloneIfPossible(value)) : promise;
  }

  /**
   * Execute fetch request with retry logic
   */
  async fetchWithRetry(
    url: string,
    options: RequestInit = {},
    config: Partial<RetryConfig> = {}
  ): Promise<Response> {
    const metadata: RequestMetadata = {
      url,
      method: options.method || 'GET',
      timestamp: Date.now(),
      attempt: 0
    };

    return this.executeWithRetry(async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Check for HTTP errors
        if (!response.ok) {
          const code: RetryErrorCode | undefined =
            response.status >= 500
              ? 'SERVER_ERROR'
              : response.status === 429
              ? 'RATE_LIMITED'
              : response.status === 503
              ? 'UNAVAILABLE'
              : undefined;

          throw enhanceError(new Error(`HTTP ${response.status}: ${response.statusText}`), {
            status: response.status,
            statusText: response.statusText,
            code,
          });
        }

        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        
        // Classify network errors
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            enhanceError(error, { code: 'TIMEOUT' });
          } else if (error.message.includes('fetch')) {
            enhanceError(error, { code: 'NETWORK_ERROR' });
          }
        }
        
        throw error;
      }
    }, config, metadata);
  }

  /**
   * Execute GraphQL request with retry logic
   */
  async graphqlWithRetry<T>(
    url: string,
    query: string,
    variables?: Record<string, unknown>,
    headers?: Record<string, string>,
    config: Partial<RetryConfig> = {}
  ): Promise<T> {
    const requestOptions: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      body: JSON.stringify({
        query,
        variables: variables || {}
      })
    };

    return this.executeWithRetry(async () => {
      const response = await this.fetchWithRetry(url, requestOptions, config);
      const result = (await response.json()) as GraphQLResponse<T>;

      // Handle GraphQL errors
      if (result.errors && result.errors.length > 0) {
        const error = enhanceError(
          new Error(`GraphQL error: ${result.errors.map((graphQLError) => graphQLError.message).join(', ')}`),
          {
            code: 'GRAPHQL_ERROR',
            graphqlErrors: result.errors,
          }
        );
        
        // Check for retryable GraphQL errors
        const hasRetryableError = result.errors.some((graphQLError) => 
          graphQLError.extensions?.code === 'THROTTLED' ||
          graphQLError.extensions?.code === 'INTERNAL_ERROR' ||
          graphQLError.message.includes('rate limit') ||
          graphQLError.message.includes('temporarily unavailable')
        );
        
        if (hasRetryableError) {
          enhanceError(error, { code: 'THROTTLED' });
        }
        
        throw error;
      }

      if (!result.data) {
        throw enhanceError(new Error('No data returned from GraphQL query'), { code: 'NO_DATA' });
      }

      return result.data;
    }, config, {
      url,
      method: 'POST',
      timestamp: Date.now(),
      attempt: 0
    });
  }

  /**
   * Check if error should trigger retry
   */
  private shouldRetryError(error: Error, attempt: number, config: RetryConfig): boolean {
    // Use custom retry logic if provided
    if (config.shouldRetry) {
      return config.shouldRetry(error, attempt);
    }

    // Check error code
    const errorCode = getErrorCode(error);
    if (errorCode && config.retryableErrors.includes(errorCode)) {
      return true;
    }

    // Check error message for retryable patterns
    const errorMessage = error.message.toLowerCase();
    const retryablePatterns = [
      'network error',
      'timeout',
      'rate limit',
      'throttled',
      'unavailable',
      'internal error',
      'server error',
      'connection',
      'fetch'
    ];

    return retryablePatterns.some(pattern => errorMessage.includes(pattern));
  }

  /**
   * Calculate delay with exponential backoff and jitter
   */
  private calculateDelay(attempt: number, config: RetryConfig): number {
    const exponentialDelay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1);
    const jitter = Math.random() * 0.1 * exponentialDelay; // Add 10% jitter
    const delay = Math.min(exponentialDelay + jitter, config.maxDelay);
    
    return Math.floor(delay);
  }

  /**
   * Sleep for specified milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Generate unique key for request tracking
   */
  private generateRequestKey(metadata?: Partial<RequestMetadata>): string | null {
    if (!metadata?.url) {
      return null;
    }

    return `${metadata.method || 'GET'}:${metadata.url}`;
  }

  /**
   * Check if circuit breaker is open
   */
  private isCircuitOpen(key: string): boolean {
    const breaker = this.circuitBreakers.get(key);
    if (!breaker) {
      return false;
    }

    if (breaker.state === 'OPEN') {
      // Check if timeout has passed
      if (Date.now() - breaker.lastFailureTime > this.circuitBreakerTimeout) {
        breaker.state = 'HALF_OPEN';
        return false;
      }
      return true;
    }

    return false;
  }

  /**
   * Record failure for circuit breaker
   */
  private recordFailure(key: string): void {
    const breaker = this.circuitBreakers.get(key) || {
      failures: 0,
      lastFailureTime: 0,
      state: 'CLOSED' as const
    };

    breaker.failures++;
    breaker.lastFailureTime = Date.now();

    if (breaker.failures >= this.circuitBreakerThreshold) {
      breaker.state = 'OPEN';
      debugLog(`Circuit breaker opened for ${key} after ${breaker.failures} failures`);
    }

    this.circuitBreakers.set(key, breaker);
  }

  /**
   * Reset circuit breaker on success
   */
  private resetCircuitBreaker(key: string): void {
    const breaker = this.circuitBreakers.get(key);
    if (breaker) {
      breaker.failures = 0;
      breaker.state = 'CLOSED';
      this.circuitBreakers.set(key, breaker);
    }
  }

  /**
   * Get circuit breaker status for monitoring
   */
  getCircuitBreakerStatus(): Record<string, CircuitBreakerState> {
    const status: Record<string, CircuitBreakerState> = {};
    this.circuitBreakers.forEach((state, key) => {
      status[key] = { ...state };
    });
    return status;
  }

  /**
   * Clear all pending requests (useful for cleanup)
   */
  clearPendingRequests(): void {
    this.pendingRequests.clear();
  }

  /**
   * Reset all circuit breakers
   */
  resetAllCircuitBreakers(): void {
    this.circuitBreakers.clear();
  }
}

// Default export - singleton instance
const apiRetryService = new ApiRetryService();
export default apiRetryService;

// Named exports
export { apiRetryService };
